import type { EmailService } from "./email-service";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

// Top-of-funnel analytics only. Consent Mode v2 keeps analytics_storage denied
// until the banner is accepted, so a conversion count built here would carry an
// unknown undercount — enquiry volume is counted on the receiving SNS topic
// instead. Never pass message text or the visitor's email address in here.
const trackEvent = (name: string, params?: Record<string, string>): void => {
  if (typeof window.gtag === "function") {
    window.gtag("event", name, params ?? {});
  }
};

// Where the visitor came from, for context in the outgoing mail. An explicit
// ?from=/#from= wins over the referrer so a campaign or an entry point on the
// page can name itself.
// ?from=, #from= and the referrer are all visitor-controlled and land in the
// outgoing mail body, where a newline would let a sender forge a "Message:"
// line below their own. Strip line breaks and cap the length before it leaves
// this function, so every caller gets the safe form.
const sanitiseContext = (raw: string): string =>
  raw.replace(/[\r\n]+/g, " ").trim().slice(0, 200);

const getSourceContext = (): string => {
  const fromQuery = new URLSearchParams(window.location.search).get("from");
  if (fromQuery) {
    return sanitiseContext(fromQuery);
  }
  const hashMatch = window.location.hash.match(/[#&]from=([^&]+)/);
  if (hashMatch?.[1]) {
    // decodeURIComponent throws URIError on a malformed percent-sequence, and
    // a bare "%" is enough. This function runs before the panel is opened and
    // before the submit handler's try block, so an uncaught throw here stops
    // the form opening at all and makes Send do nothing, silently. Fall back
    // to the raw capture rather than let that happen.
    try {
      return sanitiseContext(decodeURIComponent(hashMatch[1]));
    } catch {
      return sanitiseContext(hashMatch[1]);
    }
  }
  if (document.referrer) {
    try {
      const ref = new URL(document.referrer);
      // Our own pages are the useful signal: which entry point or case the
      // visitor came from. A third-party referrer is reduced to its origin,
      // because its query string can carry PII we have no reason to receive
      // or to forward to GA4.
      return ref.origin === window.location.origin
        ? sanitiseContext(ref.pathname + ref.hash)
        : ref.origin;
    } catch {
      return "unknown";
    }
  }
  return "unknown";
};

window.addEventListener("DOMContentLoaded", () => {
  // The AWS SDK (Cognito + SNS) is heavy (~251 KB). Load it lazily on first
  // submit via a dynamic import so Parcel code-splits it out of the initial
  // homepage bundle. The instance is cached after the first creation.
  let emailService: EmailService | undefined;
  const getEmailService = async (): Promise<EmailService> => {
    if (!emailService) {
      const { EmailService } = await import("./email-service");
      emailService = new EmailService();
    }
    return emailService;
  };

  const contactForm =
    document.querySelector<HTMLFormElement>("#contact-form form");
  const contactFormContainer =
    document.querySelector<HTMLDivElement>("#contact-form");
  const contactFormButtonClose = document.querySelector<HTMLButtonElement>(
    "#contact-form-button-close"
  );
  const contactFormButtonOpen = document.querySelector<HTMLButtonElement>(
    "#contact-form-button-open"
  );
  if (
    !contactForm ||
    !contactFormContainer ||
    !contactFormButtonClose ||
    !contactFormButtonOpen
  ) {
    return;
  }

  const sendButton = document.querySelector<HTMLButtonElement>(
    "#contact-form-button-send"
  );
  const honeypotInput = document.querySelector<HTMLInputElement>(
    "#contact-form-website"
  );

  // When the panel was FIRST opened. Not reset on close/reopen: closing keeps
  // what the visitor typed, so a quick send right after reopening is a person
  // finishing an earlier draft, not a bot. A submit from a panel that was
  // never opened cannot come from a person either — the form is display:none
  // until openContactForm runs.
  let formFirstOpenedAt: number | undefined;
  // 3 s: a person has to open the panel, type a valid email address (the
  // field is required) and press Send. Even with email autofill that is
  // rarely under 3 s, while a form-filling script does it in milliseconds.
  // A higher value would start to catch fast humans, and a caught human gets
  // a thank-you and no reply, so err low.
  const MIN_FILL_MS = 3000;

  // Guards against a second submit event while the first is still in flight.
  // `disabled` alone is not enough: requestSubmit(), a queued Enter keypress
  // or a script can still fire submit. The first submit lazy-loads the AWS SDK,
  // which widens the window in which an impatient second click lands
  // (2026-09-05: four identical enquiries within 2 s from one sender).
  let sending = false;

  const showThankYou = (feedback: HTMLParagraphElement | null): void => {
    if (feedback) {
      feedback.textContent =
        "Thank you. We have received your enquiry and will contact you at the email address you provided.";
    }
  };

  contactForm.addEventListener("submit", async (event) => {
    event.preventDefault();
    if (sending) {
      return;
    }
    const emailInput =
      document.querySelector<HTMLInputElement>("#contact-form-email");
    const messageInput = document.querySelector<HTMLInputElement>(
      "#contact-form-message"
    );
    if (!emailInput || !messageInput) {
      return;
    }
    const needInput = document.querySelector<HTMLSelectElement>(
      "#contact-form-need"
    );
    let email = emailInput.value;
    let message = messageInput.value;
    // Optional: an empty selection is a valid submission and must not block it.
    const need = needInput?.value ?? "";
    let subject = `[Eyevinn Contact Form] <${email}> sent you a message`;
    let mailBody = "";
    mailBody += `From: ${email}\n`;
    mailBody += `Enquiry about: ${sanitiseContext(need) || "not specified"}\n`;
    mailBody += `Came from: ${getSourceContext()}\n`;
    mailBody += `User Agent: ${window.navigator.userAgent}\n`;
    mailBody += "\n";
    mailBody += `Message: ${message}\n`;

    const userFeedback = document.querySelector<HTMLParagraphElement>(
      "#contact-form-user-feedback"
    );

    // Silent rejection: same thank-you as a real send, nothing published, so
    // a bot learns nothing from the response. The GA event carries only the
    // reason — no field values — and goes through trackEvent like every other
    // event on the site, so it follows the same Consent Mode v2 setup
    // (index.html: without consent GA4 still receives a cookieless ping). It is
    // how we would notice a real visitor being caught.
    const blockedReason =
      honeypotInput && honeypotInput.value !== ""
        ? "honeypot"
        : formFirstOpenedAt === undefined ||
            Date.now() - formFirstOpenedAt < MIN_FILL_MS
          ? "too_fast"
          : "";
    if (blockedReason) {
      trackEvent("contact_blocked", { reason: blockedReason });
      showThankYou(userFeedback);
      contactForm.reset();
      return;
    }

    sending = true;
    if (sendButton) {
      sendButton.disabled = true;
    }
    if (userFeedback) {
      userFeedback.textContent = "Sending…";
    }
    try {
      const service = await getEmailService();
      await service.sendEmail("contactform", subject, mailBody);
      showThankYou(userFeedback);
      contactForm.reset();
    } catch (err) {
      console.error(err);
      if (userFeedback) {
        userFeedback.textContent =
          "There was an issue sending your message, contact webmaster@eyevinn.se if the problem persists.";
      }
    } finally {
      sending = false;
      if (sendButton) {
        // Re-enabled after success too: the form has been reset by then and
        // the required email field stops an empty resend, while a visitor
        // with a second question must still be able to send it.
        sendButton.disabled = false;
        // Disabling the button drops keyboard focus to <body>; put it back on
        // Send so a keyboard user is not sent back to the top of the page.
        if (document.activeElement === document.body) {
          sendButton.focus();
        }
      }
    }
  });

  const needSelect = document.querySelector<HTMLSelectElement>(
    "#contact-form-need"
  );
  needSelect?.addEventListener("change", () => {
    if (needSelect.value) {
      trackEvent("need_selected", { need: needSelect.value });
    }
  });

  const openContactForm = () => {
    trackEvent("contact_opened", { source: getSourceContext() });
    formFirstOpenedAt ??= Date.now();
    contactFormContainer.className = "expanded";
    contactForm.style.display = "block";
    contactFormButtonOpen.style.display = "none";
    contactFormContainer.querySelector<HTMLInputElement>("input")?.focus();
  };

  contactFormButtonClose.addEventListener("click", () => {
    contactFormContainer.className = "collapsed";
    contactForm.style.display = "none";
    contactFormButtonOpen.style.display = "block";
    contactFormButtonOpen.focus();
    // Leaving the fragment in the address bar would reopen the form on the
    // next reload, and a second click on the same link would not fire
    // hashchange. replaceState rather than assignment, so closing the form
    // does not add a history entry to walk back through.
    if (window.location.hash === "#contact-form") {
      history.replaceState(
        null,
        "",
        window.location.pathname + window.location.search
      );
    }
  });
  contactFormButtonOpen.addEventListener("click", openContactForm);

  contactFormContainer.className = "collapsed";
  contactForm.style.display = "none";
  contactFormButtonOpen.style.display = "block";

  // Every service page links to "/#contact-form". Without this the visitor
  // lands at the top of the homepage with the form still collapsed into its
  // corner tab, which is not a call to action. index.html's own buttons use an
  // inline onclick that a subpage cannot run, so the hash is the only signal
  // that survives the navigation. Runs after the collapse above, which would
  // otherwise close what we just opened.
  const openIfHashTargetsForm = (deferFocus: boolean) => {
    if (window.location.hash !== "#contact-form") {
      return;
    }
    openContactForm();
    if (deferFocus) {
      // On a fresh load the browser sets the sequential focus navigation
      // starting point from the fragment once the document is parsed, which
      // takes focus straight back off the field openContactForm just moved it
      // to. Re-focus on the next frame so the visitor arrives in the email
      // field rather than on the container.
      requestAnimationFrame(() => {
        contactFormContainer.querySelector<HTMLInputElement>("input")?.focus();
      });
    }
  };
  openIfHashTargetsForm(true);
  window.addEventListener("hashchange", () => openIfHashTargetsForm(false));
});
